import React, { useState } from 'react';
import { Grid, Typography, Avatar, Box, TextField, Button, FormControl } from '@mui/material';
import { useHistory } from 'react-router-dom';
import jimboPalette from '../../jimboTheme/palette';
import debounce from 'lodash.debounce';

import { signInWithGoogle, useLazyCheckuserQuery, useCreateUserMutation, recaptchaVerify, useVerifyUserMutation } from '@services';

import { setActiveUser } from '@store/slices/users';
import { useAppDispatch } from '@hooks/redux-custom';
import { setUserInfo } from '@store/slices/authentication';
import images from '../../assets/images';
import { User } from '@models';
import { RouteModules as routes } from '../../routes';

const Login = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [img, setImg] = useState('');
  const [facode, setFacode] = useState('');
  const [currentUser, setCurrentUser] = useState<User>({ email: '' });
  const [isErrorCode, setIsErrorCode] = useState(false);
  const [userToken, setUserToken] = useState('');
  const [showCode, setShowCode] = useState(true);

  const [checkUser] = useLazyCheckuserQuery();
  const [createUser] = useCreateUserMutation();
  const [verifyUser] = useVerifyUserMutation();

  const recaptchaVerification = async () => {
    try {
      const CallBack = (token: string) => {
        if (token) googleSignIn();
      };

      await recaptchaVerify(CallBack);
    } catch (err) {
      console.log('===recaptcha verification error===', err);
    }
  };

  const verify2FA = async (evt: { preventDefault: () => void }) => {
    evt.preventDefault();

    try {
      setIsErrorCode(false);
      const value: any = await verifyUser({
        email: currentUser.email,
        code: facode,
      });

      if (value.data.code === 200) {
        if (currentUser.isActive) {
          dispatch(setActiveUser(currentUser));
          dispatch(setUserInfo(userToken));
          localStorage.setItem('activeUser', JSON.stringify(currentUser));
          const moduleName = currentUser?.roles?.find((role) => role.role)?.moduleName ?? '';
          const path = routes.find((route) => route.module === moduleName && route.isLandingPage)?.path ?? '';

          history.push(path);
        } else {
          history.push('/welcome');
        }
      } else {
        setIsErrorCode(true);
      }
    } catch (err) {
      setIsErrorCode(true);
    }
  };

  const googleSignIn = async () => {
    try {
      const response = await signInWithGoogle();

      if (response?.user) {
        const { user } = response;

        const token = await user.getIdToken(true);
        const emailValid = /@jimmybrings.com.au|@edg.com.au|@deity.io/.test(user.email!);

        // check if the user Exist, if exists, then redirect to dashboard or else redirect to welcome page
        const userResult = await checkUser(user.uid);

        if (userResult.data?.isActive) {
          setUserToken(response.user.refreshToken);

          localStorage.setItem('token', user.refreshToken);
          localStorage.setItem('idToken', token);
          localStorage.setItem('tokenStoreTime', new Date().getTime().toString());
          localStorage.setItem('activeUser', JSON.stringify(userResult.data));

          if (userResult?.data?.qr) {
            setImg(userResult?.data.qr ?? '');
            setShowCode(false);

            setCurrentUser({
              email: user.email ?? '',
            });
          }

          setCurrentUser(userResult.data);
          console.log(userResult.data);
        } else if (!userResult.data?.email && emailValid) {
          // create the user
          const getUser: any = await createUser({
            firebaseId: user.uid,
            email: user.email ?? '',
            firstName: user.displayName ?? '',
            lastName: '',
          });

          if (getUser.data.qr) {
            setImg(getUser.data.qr);

            setCurrentUser({
              email: user.email ?? '',
            });
          }
        } else {
          // The user is not active with 2FA
          if (userResult?.data?.qr) {
            setImg(userResult?.data.qr ?? '');

            setCurrentUser({
              email: user.email ?? '',
            });
          }

          setShowCode(false);
        }
      } else {
        history.push('/restricted');
      }
    } catch (error: any) {
      console.log('===signin error===', error);
    }
  };

  return (
    <Grid container justifyContent="center" alignItems="center" minHeight="100vh" sx={{ flexDirection: 'column' }}>
      <Box sx={{ position: 'fixed', bottom: 0, right: 0 }}>
        <img src={images.scott} alt="Scott" height="400" width="500" data-testid="scott-image" />
      </Box>
      <Box sx={{ position: 'fixed', bottom: 300, right: 250 }}>
        <img src={images.SpeechBubble} alt="speech bubble" height="280" width="280" />
        <Typography sx={{ position: 'absolute', top: 75, right: 70 }} variant={'h6'}>
          Welcome to Jimbo
          <br />
          ...My Secret Local!
        </Typography>
      </Box>

      <Box
        pb={2}
        sx={{
          width: '400px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0px 1px 1px 1px #ddd',
          zIndex: 1,
          backgroundColor: '#fff',
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            <Box
              sx={{
                display: 'flex',
                px: '10px',
                backgroundColor: jimboPalette.primary.main,
                justifyContent: 'center',
                alignItems: 'center',
                borderTop: 1,
                borderLeft: 1,
                borderRight: 1,
                borderColor: 'grey.300',
                py: '5px',
              }}
            >
              <Typography variant={'h2'} sx={{ fontSize: '50px', alignSelf: 'center' }}>
                JIMBO
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} textAlign="center">
            <Button
              onClick={debounce(recaptchaVerification, 300)}
              type="button"
              sx={{
                backgroundColor: '#fafafa',
                mt: 3,
                mb: 3,
                border: 0.5,
                borderRadius: '5px',
              }}
              variant={'contained'}
              startIcon={<Avatar src={'https://img.icons8.com/color/48/undefined/google-logo.png'} />}
              data-testid="login-google-signin-btn"
            >
              GOOGLE SIGNIN
            </Button>
          </Grid>
          <Grid item xs={12} textAlign="center">
            <div id="recaptcha-container" data-size="invisible" style={{ margin: '0 auto', width: '310px' }}></div>
          </Grid>

          {img && (
            <Grid item xs={12} textAlign="center" p={2}>
              {showCode && (
                <>
                  <img src={img} alt="google-signin" />
                  <Typography variant="body2">
                    Scan the QR Code in the Authenticator app then enter the code that you see in the app in the text field and click
                    Submit.
                  </Typography>
                </>
              )}
              {!showCode && (
                <Button onClick={() => setShowCode(true)} variant="outlined" data-testid="login-show-qr-code-btn">
                  Show QR code
                </Button>
              )}
            </Grid>
          )}
          {currentUser.email && (
            <form
              onSubmit={verify2FA}
              style={{
                width: '100%',
              }}
            >
              <Grid item xs={12} textAlign="center" p={2}>
                <FormControl>
                  <TextField
                    name="2fa-code"
                    label="2FA Code"
                    placeholder="Enter the code that you see in the Authenticator app"
                    value={facode}
                    onChange={(e) => setFacode(e.target.value)}
                  ></TextField>
                </FormControl>
              </Grid>
              <Grid item xs={12} textAlign="center" p={2}>
                <Button variant="contained" type="submit" data-testid="login-verify-2fa-btn">
                  Verify 2FA
                </Button>
              </Grid>
              <Grid item xs={12} textAlign="center" p={2}>
                {isErrorCode && (
                  <Typography variant="body2" color="red">
                    Wrong 2FA code? Try again!{' '}
                  </Typography>
                )}
              </Grid>
            </form>
          )}
        </Grid>
      </Box>
    </Grid>
  );
};

export default Login;
